/**
 * ModuleRatingButton — iter1114.b
 *
 * Compact one-tap rating widget placed inside each twin module (BHI,
 * Dental, Cardio, etc.) so happy users can leave a contextual review
 * right where they're delighted. Submits to /feedback/submit with a
 * `module_context` tag so admins can see WHICH surface worked.
 *
 * UX:
 *   - Initial state:   inline bar with "⭐ Хареса ли ти този модул?"
 *   - After 1st star:  expand to 5-star selector + optional 1-line text
 *   - After submit:    "Благодарим! 🙏" state for 3s then reset
 *
 * Props:
 *   - moduleKey: string — short id (e.g. "bhi", "cardio_twin", "dental")
 *   - moduleName: string — display name (BG) for the thank-you message
 *   - isDark: boolean
 */
import React, { useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { Star } from "lucide-react";
import { toast } from "sonner";

export default function ModuleRatingButton({ moduleKey, moduleName, isDark = false }) {
  const { API } = useAuth();
  const [hoverStar, setHoverStar] = useState(0);
  const [chosen, setChosen] = useState(0);
  const [text, setText] = useState("");
  const [allowPublish, setAllowPublish] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const pickStar = (n) => {
    setChosen(n);
    setExpanded(true);
  };

  const submit = async () => {
    if (chosen === 0) return;
    setSending(true);
    try {
      await axios.post(`${API}/feedback/submit`, {
        category: "review",
        rating: chosen,
        text: text.trim(),
        allow_publish: allowPublish,
        module_context: moduleKey,
      });
      setSent(true);
      toast.success(allowPublish
        ? "Благодарим Ви! Отзивът Ви ще бъде прегледан от екипа."
        : "Благодарим Ви за оценката!");
      setTimeout(() => {
        setSent(false); setExpanded(false); setChosen(0); setText(""); setAllowPublish(false);
      }, 3000);
    } catch {
      toast.error("Грешка при изпращането");
    } finally {
      setSending(false);
    }
  };

  const cardCls = isDark
    ? "bg-slate-900/60 border-slate-800"
    : "bg-white border-slate-200";

  if (sent) {
    return (
      <div
        className={`p-3 rounded-xl border ${cardCls} text-center`}
        data-testid={`module-rating-sent-${moduleKey}`}
      >
        <div className="text-xl">🙏</div>
        <p className="text-xs font-semibold mt-1">
          Благодарим Ви за оценката на {moduleName}!
        </p>
      </div>
    );
  }

  return (
    <div
      className={`p-3 rounded-xl border ${cardCls}`}
      data-testid={`module-rating-${moduleKey}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-semibold shrink-0">
          Хареса ли Ви {moduleName}?
        </span>
        <div
          className="flex items-center gap-0.5"
          onMouseLeave={() => setHoverStar(0)}
          data-testid={`module-rating-stars-${moduleKey}`}
        >
          {[1, 2, 3, 4, 5].map((s) => {
            const fill = (hoverStar || chosen) >= s;
            return (
              <button
                key={s}
                type="button"
                onMouseEnter={() => setHoverStar(s)}
                onClick={() => pickStar(s)}
                aria-label={`${s} звезди`}
                className="p-0.5"
                data-testid={`module-rating-star-${moduleKey}-${s}`}
              >
                <Star
                  className={`w-5 h-5 transition-transform duration-150 ${
                    fill ? "text-amber-500 fill-amber-500 scale-110" : "opacity-30"
                  }`}
                />
              </button>
            );
          })}
        </div>
      </div>

      {expanded && chosen > 0 && (
        <div className="mt-2 space-y-2" data-testid={`module-rating-expanded-${moduleKey}`}>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Какво точно Ви хареса (или не)? (незадължително)"
            className={`w-full h-14 p-2 text-[11px] rounded-md border resize-none ${
              isDark
                ? "bg-slate-950 border-slate-700 text-white placeholder:text-slate-500"
                : "border-slate-200 placeholder:text-slate-400"
            }`}
            data-testid={`module-rating-text-${moduleKey}`}
          />
          <label className="flex items-start gap-1.5 cursor-pointer select-none">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={allowPublish}
              onChange={(e) => setAllowPublish(e.target.checked)}
              data-testid={`module-rating-publish-${moduleKey}`}
            />
            <span className={`text-[10px] leading-snug ${isDark ? "text-slate-400" : "text-slate-600"}`}>
              Съгласен/-на съм отзивът ми да се покаже публично на главната
              страница (само лично име).
            </span>
          </label>
          <div className="flex items-center gap-2">
            <button
              onClick={submit}
              disabled={sending}
              className="px-3 py-1.5 text-[11px] font-semibold rounded-md bg-cyan-500 text-white hover:bg-cyan-600 disabled:opacity-50"
              data-testid={`module-rating-submit-${moduleKey}`}
            >
              {sending ? "Изпращане…" : "Изпрати"}
            </button>
            <button
              onClick={() => { setExpanded(false); setChosen(0); setText(""); }}
              className="text-[11px] opacity-60 hover:opacity-100"
              data-testid={`module-rating-cancel-${moduleKey}`}
            >
              Отказ
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
